import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Upload, Loader2, CheckCircle2, XCircle, FileText, UserPlus } from 'lucide-react';
import Navigation from '@/components/landing/Navigation';
import Footer from '@/components/landing/Footer';
import FloatingActionButtons from '@/components/shared/FloatingActionButtons';
import Seo from '@/components/shared/Seo';
import { trackTutorApplication } from '@/lib/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

/**
 * V4 PLATFORM - TUTOR APPLICATION
 * Complete clean rebuild with premium Winhouse brand styling (Phase 3B)
 * Uses global Navigation and Footer for consistency across platform
 */
const TutorApplicationV4 = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showFloatingButtons, setShowFloatingButtons] = useState(false);
  
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    subjects: '',
    notes: ''
  });
  
  const [cvFile, setCvFile] = useState(null);
  const [cvFileName, setCvFileName] = useState('');
  const [fileError, setFileError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    
    if (!file) {
      setCvFile(null);
      setCvFileName('');
      setFileError('');
      return;
    }
    
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    const allowedTypes = ['.pdf', '.doc', '.docx'];
    
    if (!allowedTypes.includes(ext)) {
      setFileError('Invalid file type. Please upload PDF, DOC, or DOCX');
      setCvFile(null);
      setCvFileName('');
      e.target.value = '';
      return;
    }
    
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      setFileError(`File too large. Max 10MB (current: ${(file.size / (1024 * 1024)).toFixed(1)}MB)`);
      setCvFile(null);
      setCvFileName('');
      e.target.value = '';
      return;
    }
    
    setCvFile(file);
    setCvFileName(file.name);
    setFileError('');
  };
  
  const validateForm = () => {
    if (!formData.fullName.trim()) {
      toast.error('Please enter your full name');
      return false;
    }
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return false;
    }
    if (!formData.phone.trim()) {
      toast.error('Please enter your phone number');
      return false;
    }
    if (!formData.subjects.trim()) {
      toast.error('Please enter subjects you can teach');
      return false;
    }
    if (!cvFile) {
      toast.error('Please upload your CV');
      return false;
    }
    return true;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('full_name', formData.fullName.trim());
      formDataToSend.append('email', formData.email.trim());
      formDataToSend.append('phone', formData.phone.trim());
      formDataToSend.append('subjects', formData.subjects.trim());
      formDataToSend.append('cv_file', cvFile);
      if (formData.notes.trim()) {
        formDataToSend.append('notes', formData.notes.trim());
      }
      
      const response = await fetch(`${BACKEND_URL}/api/v4/tutor/apply`, {
        method: 'POST',
        body: formDataToSend
      });
      
      const contentType = response.headers.get('content-type');
      let data;
      
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        const text = await response.text();
        throw new Error(`Server error: ${text.substring(0, 100)}`);
      }
      
      if (!response.ok) {
        throw new Error(data.detail || data.message || 'Submission failed');
      }
      
      toast.success(data.message || 'Application submitted successfully!');
      try {
        trackTutorApplication({
          subjects: formData.subjects,
          has_cv: !!cvFile,
        });
      } catch { /* noop */ }
      
      setTimeout(() => {
        navigate('/application-success', {
          state: {
            applicantName: formData.fullName,
            applicationId: data.tutor_id
          }
        });
      }, 1000);
      
    } catch (error) {
      console.error('Submission error:', error);
      if (error.message.includes('Failed to fetch')) {
        toast.error('Network error. Please check your connection and try again.');
      } else {
        toast.error(error.message || 'Failed to submit application. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-white">
      <Seo
        title="Become a Tutor | Winhouse Tutors"
        description="Join Winhouse Tutors and start teaching students globally."
        path="/become-tutor"
      />
      {/* Global Navigation - Same as Home, Services, Pricing */}
      <Navigation mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} />
      
      {/* Enhanced Hero Section - VCE Tutor Recruitment Style */}
      <div className="bg-gradient-to-br from-gray-50 via-white to-stone-50 py-20" style={{paddingTop: '7rem'}}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-extrabold mb-6 text-gray-900 leading-tight">
              Join Winhouse Tutors as a VCE Tutor
            </h1>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto mb-10 leading-relaxed">
              Work with motivated students, teach what you do best, and grow with a premium tutoring brand. Earn competitive commission while making a real impact on VCE students' academic success.
            </p>
            
            {/* Primary CTA Button */}
            <Button
              onClick={() => {
                document.getElementById('application-form')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="text-lg font-bold px-10 py-7 rounded-full shadow-2xl hover:shadow-3xl transition-all duration-300 hover:scale-105"
              style={{
                background: 'linear-gradient(135deg, #B7BF72 0%, #a8b565 100%)',
                color: '#000000'
              }}
            >
              <UserPlus className="w-6 h-6 mr-3" />
              Apply to Become a Tutor
            </Button>
          </div>
        </div>
      </div>
      
      {/* VCE Subjects We Need Tutors For Section */}
      <div className="bg-white py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-block mb-4">
              <div className="h-1 w-16 mx-auto" style={{background: 'linear-gradient(90deg, #006C69 0%, #B7BF72 100%)'}}></div>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">
              VCE Subjects We Need Tutors For
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We're actively recruiting tutors across all major VCE subjects.
            </p>
          </div>
          
          {/* Subject Pills */}
          <div className="flex flex-wrap justify-center gap-3 max-w-4xl mx-auto">
            {[
              'English',
              'EAL',
              'Selective Entry',
              'Mathematical Methods',
              'Specialist Maths',
              'General Maths',
              'Further Maths',
              'Biology',
              'Chemistry',
              'Physics',
              'Accounting',
              'Economics',
              'Business Management',
              'Psychology',
              'Legal Studies',
              'and more...'
            ].map((subject, index) => (
              <div
                key={index}
                className="px-6 py-3 rounded-full border-2 transition-all duration-300 hover:shadow-md"
                style={{
                  borderColor: '#006C69',
                  background: 'rgba(0, 108, 105, 0.04)',
                  color: '#333333',
                  fontSize: '0.95rem',
                  fontWeight: '500'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(0, 108, 105, 0.08)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(0, 108, 105, 0.04)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                {subject}
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* Application Form */}
      <div id="application-form" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <Card className="shadow-xl bg-white" style={{border: '1px solid rgba(183, 191, 114, 0.20)', borderRadius: '12px'}}>
          <CardHeader className="space-y-2 text-white rounded-t-xl" style={{background: '#000000'}}>
            <CardTitle className="text-3xl font-bold">Application Form</CardTitle>
            <CardDescription className="text-base" style={{color: '#CCCCCC'}}>
              Share your knowledge and help students achieve their goals
            </CardDescription>
          </CardHeader>
          
          <CardContent className="p-10 bg-gradient-to-br from-white via-gray-50/30 to-white">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="fullName" className="text-base font-semibold">
                  Full Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="fullName"
                  name="fullName"
                  type="text"
                  placeholder="John Smith"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  className="h-12 text-base"
                  disabled={isSubmitting}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email" className="text-base font-semibold">
                  Email Address <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="john@example.com"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="h-12 text-base"
                  disabled={isSubmitting}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-base font-semibold">
                  Phone Number <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder="+61 400 000 000"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="h-12 text-base"
                  disabled={isSubmitting}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="subjects" className="text-base font-semibold">
                  Subjects You Can Teach <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="subjects"
                  name="subjects"
                  type="text"
                  placeholder="e.g., Mathematics, English, Physics"
                  value={formData.subjects}
                  onChange={handleInputChange}
                  className="h-12 text-base"
                  disabled={isSubmitting}
                />
                <p className="text-sm text-gray-500">Separate multiple subjects with commas</p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="cvFile" className="text-base font-semibold">
                  Upload CV/Resume <span className="text-red-500">*</span>
                </Label>
                <div className="border-2 border-dashed rounded-lg p-8 text-center transition-all" style={{borderColor: 'rgba(180, 200, 150, 0.30)'}} onMouseEnter={(e) => e.currentTarget.style.borderColor = 'rgba(180, 200, 150, 0.50)'} onMouseLeave={(e) => e.currentTarget.style.borderColor = 'rgba(180, 200, 150, 0.30)'}>
                  <input
                    id="cvFile"
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileChange}
                    className="hidden"
                    disabled={isSubmitting}
                  />
                  <label htmlFor="cvFile" className="cursor-pointer flex flex-col items-center gap-3">
                    {cvFileName ? (
                      <>
                        <FileText className="h-12 w-12" style={{color: '#006C69'}} />
                        <div>
                          <p className="text-sm font-semibold text-gray-900">{cvFileName}</p>
                          <p className="text-xs text-gray-500 mt-1">Click to change file</p>
                        </div>
                      </>
                    ) : (
                      <>
                        <Upload className="h-12 w-12" style={{color: '#666666'}} />
                        <div>
                          <p className="text-sm font-semibold text-gray-700">Click to upload CV</p>
                          <p className="text-xs text-gray-500 mt-1">PDF, DOC, or DOCX (max 10MB)</p>
                        </div>
                      </>
                    )}
                  </label>
                </div>
                {fileError && (
                  <div className="flex items-center gap-2 text-red-600 text-sm mt-2">
                    <XCircle className="h-4 w-4" />
                    <span>{fileError}</span>
                  </div>
                )}
                {cvFileName && !fileError && (
                  <div className="flex items-center gap-2 text-sm mt-2" style={{color: '#006C69'}}>
                    <CheckCircle2 className="h-4 w-4" />
                    <span>File ready to upload</span>
                  </div>
                )}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="notes" className="text-base font-semibold">
                  Additional Notes (Optional)
                </Label>
                <Textarea
                  id="notes"
                  name="notes"
                  placeholder="Tell us about your teaching experience, qualifications, or any other relevant information..."
                  value={formData.notes}
                  onChange={handleInputChange}
                  rows={4}
                  className="text-base resize-none"
                  disabled={isSubmitting}
                />
              </div>
              
              <Button
                type="submit"
                disabled={isSubmitting || !cvFile}
                className="w-full h-16 text-lg font-bold shadow-2xl hover:shadow-3xl transition-all duration-300 hover:scale-[1.02]"
                style={{
                  background: 'linear-gradient(135deg, #B7BF72 0%, #a8b565 100%)',
                  color: '#000000'
                }}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Submitting Application...
                  </>
                ) : (
                  <>
                    <UserPlus className="mr-2 h-5 w-5" />
                    Apply to Become a Tutor
                  </>
                )}
              </Button>
              
              {/* Premium quality indicator */}
              <div className="text-center mt-4 p-4 rounded-lg" style={{background: 'rgba(183, 191, 114, 0.08)', border: '1px solid rgba(183, 191, 114, 0.20)'}}>
                <p className="text-sm font-medium" style={{color: '#333333'}}>
                  🌟 Join hundreds of qualified tutors helping students achieve excellence
                </p>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
      
      {/* Global Footer - Same as Home, Services, Pricing */}
      <Footer />
      
      {/* Shared Floating Action Buttons (Book Free Trial + WhatsApp) */}
      <FloatingActionButtons />
    </div>
  );
};

export default TutorApplicationV4;